import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    BadgePercent,
    Check,
    ChevronDown,
    Printer,
    Sparkles,
} from "lucide-react";

const PUBLIC_IMAGE_BASE = process.env.REACT_APP_IMAGE_PATH || "";

/** Avantages regroupés dans l’offre billetterie (récap en tête de page — pas répétés dans les blocs du bas). */
const BILLETTERIE_OFFER_INCLUDES = [
    {
        title: "Partenariat clair",
    },
    {
        title: "Achat express",
    },
    {
        title: "Achat sur mobile",
    },
    {
        title: "Contrôle d’accès JJ",
    },
    {
        title: "Com & Visibilité",
    },
    {
        title: "Accompagnement",
    },
];

const BILLETTERIE_PHYSICAL_OPTIONS = [
    {
        icon: Printer,
        title: "Édition et impression de billets physiques sécurisés",
        detail: "Production adaptée à vos volumes et à votre charte — modalités et tarifs ajustables en nous parlant de votre projet.",
        learnMoreTo: "/offre-billetterie#heading-ticket-physique",
    },
    {
        icon: ({ ...props }) => (
            <img
                src={`${PUBLIC_IMAGE_BASE}images/billetterie-offer-bracelet-unique.png`}
                alt="Bracelet unique"
                {...props}
                style={{ width: 40, height: 40, objectFit: "contain" }}
            />
        ),
        title: "Bracelets",
        detail: "Nous étudions le format et le chiffrage avec vous : identification sur le terrain, profils VIP / public, jour J.",
        learnMoreTo: "/offre-billetterie#heading-bracelet-unique",
    },
];

const FAQ_ITEMS = [
    {
        q: "En tant qu’organisateur, quand est-ce que je paie la commission ?",
        a: "La commission s’applique aux ventes encaissées via notre billetterie en ligne, dans les conditions fixées avec vous (contrat, taux affiché sur cette page). Vous n’avez pas d’abonnement obligatoire pour publier : vous rémunérez la plateforme au rythme de vos ventes réelles.",
    },
    {
        q: "Qu’est-ce qui est inclus dans l’offre billetterie pour mon événement ?",
        a: "Le cadre prévu couvre notamment le partenariat contractuel, l’achat express sur le site officiel, l’achat sur l'application mobile, le contrôle d’accès le jour J, la communnication et la visibilité de l'événement, ainsi qu’un accompagnement pour paramétrer et suivre votre billetterie. Les supports physiques (billets imprimés, bracelets) font l’objet d’un devis séparé, calibré avec vous.",
    },
    {
        q: "Je vends déjà ailleurs : puis-je utiliser Events en complément ?",
        a: "Oui. Beaucoup d’organisateurs ajoutent un canal de vente pour toucher un autre public ou tester une campagne. Nous vous aidons à cadrer l’offre pour que vos tarifs, quotas et messages restent cohérents avec votre stratégie globale.",
    },
    {
        q: "Comment se passent les billets papier et les bracelets ?",
        a: "Ce sont des options sur mesure : volumes, charte graphique, délais de production et logistique le jour J. Après un échange sur votre besoin, nous vous proposons une estimation claire avant commande — voir aussi les détails sur la page offre de billetterie.",
    },
    {
        q: "Qui m’accompagne si j’ai un blocage avant ou pendant l’événement ?",
        a: "Vous avez accès à une équipe réactive (selon les modalités prévues avec vous) pour les réglages billetterie, les ventes et le contrôle d’accès. En cas de pic de fréquentation ou d’imprévu, l’objectif est de vous débloquer rapidement pour ne pas impacter vos participants.",
    },
    {
        q: "Mon événement est très gros (ou multi-dates) : les tarifs peuvent-ils évoluer ?",
        a: "Oui. Les volumétries, la complexité des accès et les besoins de visibilité peuvent justifier un calibrage spécifique. Contactez-nous avec le contexte (dates, jauge, sites) : nous construisons une proposition transparente, adaptée à votre projet.",
    },
];

export default function Tarifs() {
    const [openFaq, setOpenFaq] = useState(0);
    const canonicalUrl = "https://eventsbilletterie.com/tarifs";
    const seoTitle = "Tarifs billetterie evenementielle et solutions | Events billetterie";
    const seoDescription =
        "Consultez les tarifs Events : commission billetterie, options billets physiques, bracelets, controle d'acces et accompagnement pour organisateurs d'evenements.";
    const seoKeywords =
        "tarif billetterie evenementielle, commission billetterie, prix billetterie en ligne, tarif controle d'acces, billets physiques evenement, bracelets evenement, events tarifs, billetterie, ticket facile, organisateur evenementiel, billetterie en ligne cote d'ivoire, vente de tickets evenement, gestion inscriptions, controle d'acces qr code, billets physiques evenement, bracelets unique, bracelets securises, ticket unique, ticket unique cote d'ivoire, ticket securise, ticket infalsifiable, ticket qr code, bracelet qr code, qr code unique";
    const seoImage = "https://eventsbilletterie.com/assets/images/event-details-dash.png";
    const faqStructuredData = FAQ_ITEMS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: {
            "@type": "Answer",
            text: a,
        },
    }));
    const tarifsSeoJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqStructuredData,
    };

    return (
        <main className="nc-PageAbout relative overflow-hidden">
            <Helmet>
                <html lang="fr" />
                <title>{seoTitle}</title>
                <meta name="description" content={seoDescription} />
                <meta name="keywords" content={seoKeywords} />
                <meta
                    name="robots"
                    content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
                />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="fr_CI" />
                <meta property="og:site_name" content="Events Billetterie" />
                <meta property="og:title" content={seoTitle} />
                <meta property="og:description" content={seoDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={seoImage} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoTitle} />
                <meta name="twitter:description" content={seoDescription} />
                <meta name="twitter:image" content={seoImage} />
                <meta name="twitter:site" content="@events_billetterie" />
                <link rel="canonical" href={canonicalUrl} />
                <script type="application/ld+json">
                    {JSON.stringify(tarifsSeoJsonLd)}
                </script>
            </Helmet>

            <div className="nc-BgGlassmorphism pointer-events-none absolute inset-x-0 top-0 min-h-[28rem] pl-20 py-24 flex overflow-hidden z-0">
                <span className="block bg-[#ef233c]/20 w-72 h-72 rounded-full mix-blend-multiply filter blur-3xl opacity-30 lg:w-96 lg:h-96" />
                <span className="block bg-[#04868b]/25 w-72 h-72 -ml-20 mt-24 rounded-full mix-blend-multiply filter blur-3xl opacity-30 lg:w-96 lg:h-96" />
                <span className="hidden md:block bg-primary-6000/15 w-64 h-64 ml-12 mt-40 rounded-full filter blur-3xl opacity-40" />
            </div>

            <div className="container relative z-[1] px-4 pt-12 pb-20 md:pt-16 md:pb-28">
                {/* Hero */}
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 rounded-full bg-primary-6000/10 text-primary-700 dark:text-primary-400 px-4 py-1.5 text-sm font-medium mb-6">
                        <Sparkles className="w-4 h-4" aria-hidden />
                        Tarifs &amp; transparence
                    </div>
                    <h1 className="text-4xl md:text-5xl lg:text-[3.25rem] font-bold text-neutral-900 dark:text-neutral-50 tracking-tight leading-[1.1]">
                        Une grille tarifaire qui{" "}
                        <span className="text-primary-6000">respire avec vos événements</span>
                    </h1>
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link
                            to="/nous-contacter"
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-6000 hover:bg-primary-700 text-white font-semibold px-8 py-3 text-sm md:text-base shadow-lg shadow-primary-6000/25 transition-colors"
                        >
                            Parler de mon projet
                            <ArrowRight className="w-4 h-4" aria-hidden />
                        </Link>
                        <Link
                            to="/offre-billetterie"
                            className="inline-flex items-center justify-center rounded-xl border-2 border-neutral-300 dark:border-neutral-600 text-neutral-800 dark:text-neutral-200 font-semibold px-6 py-3 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                        >
                            Voir l'offre de billetterie
                        </Link>
                    </div>
                </div>

                {/* Chiffre clé — aligné page billetterie organisateurs */}
                <div className="mt-16 md:mt-20 max-w-5xl mx-auto">
                    <div className="relative rounded-3xl border border-neutral-200/90 dark:border-neutral-700 bg-white/80 dark:bg-neutral-900/60 backdrop-blur-sm shadow-[0_24px_80px_-24px_rgba(0,0,0,0.15)] overflow-hidden">
                        <div
                            className="absolute inset-0 opacity-[0.45] dark:opacity-30 pointer-events-none"
                            style={{
                                background:
                                    "radial-gradient(ellipse 80% 60% at 50% -20%, rgb(91 207 197 / 0.35), transparent 55%)",
                            }}
                        />
                        <div className="relative grid md:grid-cols-3 gap-8 md:gap-6 p-8 md:p-12 text-center md:text-left">
                            <div className="md:col-span-1 flex flex-col items-center md:items-start justify-center">
                                <div className="inline-flex items-center gap-2 rounded-full bg-primary-6000/15 text-primary-800 dark:text-primary-300 px-3 py-1 text-xs font-semibold uppercase tracking-wide mb-3">
                                    <BadgePercent className="w-3.5 h-3.5" aria-hidden />
                                    Billetterie en ligne
                                </div> 
                                <p className="text-6xl md:text-7xl font-bold text-primary-6000 tabular-nums leading-none">
                                    5%
                                </p>
                                <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400 max-w-[14rem]">
                                    Commission sur les ventes réalisées via nos plateformes.
                                </p>
                            </div>
                            <div className="md:col-span-2 flex flex-col justify-center min-w-0">
                                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-800 bg-gray-200 p-2 rounded-md dark:text-neutral-300 mb-4">
                                    Avantages offerts avec votre billetterie
                                </p>
                                <ul className="grid sm:grid-cols-3 gap-x-8 gap-y-4">
                                    {BILLETTERIE_OFFER_INCLUDES.map(({ title }) => (
                                        <li key={title} className="flex gap-3">
                                            <Check
                                                className="w-5 h-5 text-primary-6000 shrink-0 mt-0.5"
                                                aria-hidden
                                            />
                                            <div>
                                                <p className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">
                                                    {title}
                                                </p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Supports physiques — deux cartes côte à côte */}
                <section
                    className="mt-16 md:mt-20 max-w-6xl mx-auto"
                    aria-labelledby="heading-options-physiques"
                    >
                    <div className="text-center max-w-2xl mx-auto mb-10">
                        <h2
                            id="heading-options-physiques"
                            className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-neutral-50"
                        >
                            Supports physiques sur mesure
                        </h2>
                        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
                            Tarifs et formats ajustables selon votre événement — parlons-en ou découvrez le détail sur
                            notre offre billetterie.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                        {BILLETTERIE_PHYSICAL_OPTIONS.map(({ icon: Icon, title, detail, learnMoreTo }) => (
                            <article
                                key={title}
                                className="flex flex-col rounded-3xl border border-neutral-200/90 dark:border-neutral-700 bg-white/80 dark:bg-neutral-900/50 p-8 shadow-sm hover:shadow-md transition-shadow"
                            >
                                <div className="rounded-2xl bg-gray-50 w-14 h-14 flex items-center justify-center mb-5">
                                    <Icon className="w-7 h-7 text-gray-800" aria-hidden />
                                </div>
                                <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-50">{title}</h3>
                                <p className="mt-3 text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed flex-1">
                                    {detail}
                                </p>
                                <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
                                    <Link
                                        to="/nous-contacter"
                                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-800 hover:bg-gray-900 text-white font-semibold px-5 py-2.5 text-sm shadow-md shadow-gray-800/20 transition-colors"
                                    >
                                        Nous contacter
                                        <ArrowRight className="w-4 h-4" aria-hidden />
                                    </Link>
                                    <Link
                                        to={learnMoreTo}
                                        className="inline-flex items-center justify-center rounded-xl border-2 border-neutral-300 dark:border-neutral-600 text-neutral-800 dark:text-neutral-200 font-semibold px-5 py-2.5 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                                    >
                                        En savoir plus
                                    </Link>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                {/* FAQ */}
                <section className="mt-24 md:mt-28 max-w-3xl mx-auto" aria-labelledby="heading-faq-tarifs">
                    <h2
                        id="heading-faq-tarifs"
                        className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-neutral-50 text-center mb-10"
                    >
                        Questions fréquentes
                    </h2>
                    <div className="space-y-3">
                        {FAQ_ITEMS.map(({ q, a }, index) => {
                            const isOpen = openFaq === index;
                            const panelId = `faq-tarifs-${index}`;
                            const headerId = `faq-tarifs-h-${index}`;
                            return (
                                <div
                                    key={q}
                                    className="rounded-2xl border border-neutral-200/90 dark:border-neutral-700 bg-white/90 dark:bg-neutral-900/50 shadow-sm overflow-hidden"
                                >
                                    <h3 className="text-base m-0">
                                        <button
                                            type="button"
                                            id={headerId}
                                            className="flex w-full items-center gap-3 p-4 md:p-5 text-left hover:bg-neutral-50/80 dark:hover:bg-neutral-800/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-6000 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900"
                                            onClick={() => setOpenFaq((prev) => (prev === index ? -1 : index))}
                                            aria-expanded={isOpen}
                                            aria-controls={panelId}
                                        >
                                            <span className="flex-1 font-semibold text-neutral-900 dark:text-neutral-100 pr-2">
                                                {q}
                                            </span>
                                            <ChevronDown
                                                className={`h-5 w-5 shrink-0 text-neutral-500 transition-transform duration-200 ${
                                                    isOpen ? "rotate-180" : ""
                                                }`}
                                                aria-hidden
                                            />
                                        </button>
                                    </h3>
                                    {isOpen && (
                                        <div
                                            id={panelId}
                                            role="region"
                                            aria-labelledby={headerId}
                                            className="border-t border-neutral-200/80 dark:border-neutral-700"
                                        >
                                            <p className="px-4 md:px-5 pb-4 md:pb-5 pt-3 text-neutral-600 dark:text-neutral-400 leading-relaxed text-sm md:text-base">
                                                {a}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* CTA final */}
                <div className="mt-24 max-w-3xl mx-auto text-center rounded-3xl bg-primary-6000 text-white px-8 py-12 md:py-14 shadow-xl shadow-primary-6000/20">
                    <h2 className="text-2xl md:text-5xl font-bold">Prêt à lancer votre événement ?</h2>
                    <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link
                            to="/dashboard/events/registration"
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-primary-6000 font-semibold px-8 py-3 text-sm hover:bg-neutral-100 transition-colors w-full sm:w-auto"
                        >
                            Ouvrir ma billetterie en ligne
                        </Link>
                        <Link
                            to="/nous-contacter"
                            className="inline-flex items-center justify-center rounded-full text-white font-semibold px-8 py-3 text-sm hover:bg-white/10 transition-colors w-full sm:w-auto"
                        >
                            Discuter avec un conseiller
                        </Link>
                    </div>
                </div>
            </div>
        </main>
    );
}
